import { omit } from "lodash";
import { genAgoTime } from "./helper";
import { $t } from "@/plugins/i18n";

export const DATETIME_FORMAT = "HH:mm DD/MM/YYYY";
export const DATETIME_FORMAT_SECOND = "HH:mm:ss DD/MM/YYYY";

export const PR_TYPE = {
  PR: "PR",
  NON_PR: "NON_PR",
  JIRA: "JIRA",
};

export const mapRequestCICDFields = {
  reportPeriod: "reportPeriod",
  cluster: "clusterIds",
  project: "projectIds",
  profile: "profileIds",
  application: "appIds",
  group: "groups",
  "dateRange.startDate": "fromDate",
  "dateRange.endDate": "toDate",
  startDate: "fromDate",
  endDate: "toDate",
  type: "type",
};

export const COLORS = {
  SUCCEEDED: "#2EB85C",
  FAILED: "#E55353",
  PRIMARY: "#3399FF",
  WARNING: "#F9B115",
  GRAY: "#B6B7BC",
};

export const PIPELINE_STATUS = {
  SUCCEEDED: {
    label: "SUCCEEDED",
    borderColor: COLORS.SUCCEEDED,
    backgroundColor: COLORS.SUCCEEDED,
  },
  FAILED: {
    label: "FAILED",
    borderColor: COLORS.FAILED,
    backgroundColor: COLORS.FAILED,
  },
  RUNNING: {
    label: "RUNNING",
    borderColor: COLORS.PRIMARY,
    backgroundColor: COLORS.PRIMARY,
  },
};

export const STATUS_ICON_MAP = {
  SUCCEEDED: {
    color: "var(--green)",
    opacity: "rgb(0, 204, 0, 0.3)",
    icon: "mdi-chevron-down-circle",
    text: "SUCCEEDED",
  },
  FAILED: {
    color: "var(--danger)",
    opacity: "rgb(204, 51, 0, 0.3)",
    icon: "mdi-close-circle",
    text: "FAILED",
  },
  Running: {
    color: "var(--green)",
    opacity: "rgb(0, 204, 0, 0.3)",
    icon: "mdi-chevron-down-circle",
    text: "Running",
  },
  Error: {
    color: "var(--danger)",
    opacity: "rgb(204, 51, 0, 0.3)",
    icon: "mdi-close-circle",
    text: "Error",
  },
  Undeployed: {
    color: COLORS.GRAY,
    text: "Undeployed",
  },
  Degraded: {
    color: COLORS.WARNING,
    text: "Degraded",
  },
  Completed: {
    color: COLORS.SUCCEEDED,
    text: "Completed",
  },
};

export const STATUS_HONEY_COMB = {
  Running: {
    color: COLORS.SUCCEEDED,
    text: "Running",
  },
  Error: {
    color: COLORS.FAILED,
    text: "Error",
  },
  ELSE: {
    color: COLORS.WARNING,
    text: "Undeployed",
  },
};

// data response is second
// now we need a convert unit as 60 to display minutes
// we can change to 1 to show in seconds
export const CONVERT_TIME_UNIT = 60;

// this a typical value for a duration pipeline (minutes)
export const BASE_LINE_HORIZONTAL = 10;

export const TOP_RECENT_PIPELINES_HEADER = () => [
  {
    text: $t("dashboard.running_kubernetes.label.app_name"),
    sortable: true,
    value: "appName",
    class: "header-table",
    width: "30%",
  },
  {
    text: $t("dashboard.running_kubernetes.label.build_duration"),
    sortable: true,
    value: "duration",
    class: "header-table",
    width: "35%",
  },
  {
    text: $t("dashboard.running_kubernetes.label.time"),
    sortable: true,
    value: "timestamp",
    class: "header-table",
    width: "35%",
  },
];

export const APPLICATION_HEADER = () => [
  {
    text: $t("dashboard.running_kubernetes.label.app_name"),
    sortable: true,
    value: "appName",
    class: "header-table th-app-name",
  },
  {
    text: $t("dashboard.running_kubernetes.label.status"),
    sortable: true,
    value: "status",
    class: "header-table th-status",
  },
  {
    text: $t("dashboard.base.label.profile"),
    sortable: true,
    value: "profileName",
    class: "header-table th-profile",
  },
  {
    text: $t("dashboard.running_kubernetes.label.branch_commit"),
    sortable: false,
    value: "branchCommit",
    class: "header-table th-branch-commit",
  },
  {
    text: $t("dashboard.running_kubernetes.label.image_name"),
    sortable: true,
    value: "imageName",
    class: "header-table th-image-name",
  },
  {
    text: $t("dashboard.running_kubernetes.label.image_version"),
    sortable: true,
    value: "imageTag",
    class: "header-table th-image-version",
  },
  {
    text: $t("dashboard.running_kubernetes.label.start"),
    sortable: true,
    value: "start",
    class: "header-table th-start",
  },
  {
    text: $t("dashboard.running_kubernetes.label.duration"),
    sortable: true,
    value: "duration",
    class: "header-table th-duration",
    width: "100px",
  },
];

export const TOP_CONTRIBUTOR_OF_WEEK = () => [
  {
    text: $t("dashboard.code_analysis.label.ranking"),
    sortable: false,
    value: "ranking",
    align: "center",
    class: "header-table th-ranking",
    width: 100,
  },
  {
    text: $t("dashboard.code_analysis.label.contributor"),
    sortable: false,
    value: "commitAuthor",
    class: "header-table th-status",
  },
  {
    text: $t("dashboard.code_analysis.label.commit_number"),
    sortable: false,
    value: "numberOfCommits",
    align: "center",
    class: "header-table th-profile",
    width: 145,
  },
  {
    text: $t("dashboard.code_analysis.label.added_line"),
    sortable: false,
    align: "center",
    value: "locsAdded",
    class: "header-table th-branch-commit",
    width: 110,
  },
  {
    text: $t("dashboard.code_analysis.label.deleted_line"),
    sortable: false,
    align: "center",
    value: "locsDeleted",
    class: "header-table th-image-name",
    width: 110,
  },
  {
    text: $t("dashboard.base.label.application"),
    sortable: false,
    value: "applicationName",
    class: "header-table th-image-version",
  },
];

export const TOP_CONTRIBUTOR = () => [
  {
    text: $t("dashboard.code_analysis.label.ranking"),
    sortable: false,
    value: "ranking",
    align: "center",
    class: "header-table th-ranking",
    width: 100,
  },
  {
    text: $t("dashboard.code_analysis.label.contributor"),
    sortable: false,
    value: "commitAuthor",
    class: "header-table th-status",
  },
  {
    text: $t("dashboard.code_analysis.label.commit_number"),
    sortable: false,
    value: "numberOfCommits",
    align: "center",
    class: "header-table th-profile",
    width: 145,
  },
  {
    text: $t("dashboard.code_analysis.label.added_line"),
    sortable: false,
    align: "center",
    value: "locsAdded",
    class: "header-table th-branch-commit",
    width: 110,
  },
  {
    text: $t("dashboard.code_analysis.label.deleted_line"),
    sortable: false,
    align: "center",
    value: "locsDeleted",
    class: "header-table th-image-name",
    width: 110,
  },
  {
    text: $t("dashboard.base.label.application"),
    sortable: false,
    value: "applicationName",
    class: "header-table th-image-version",
  },
];

export const TOP_REVIEWER = () => [
  {
    text: $t("dashboard.code_analysis.label.ranking"),
    sortable: false,
    value: "ranking",
    align: "center",
    class: "header-table th-ranking",
    width: 100,
  },
  {
    text: $t("dashboard.code_analysis.label.reviewer"),
    sortable: false,
    value: "reviewer",
    class: "header-table th-status",
  },
  {
    text: $t("dashboard.code_analysis.label.review_number"),
    sortable: false,
    value: "numberOfReviews",
    align: "center",
    class: "header-table th-profile",
  },
  {
    text: $t("dashboard.base.label.application"),
    sortable: false,
    value: "applicationName",
    class: "header-table th-image-version",
  },
];

export const NUMBER_OF_BUILDS_OPTIONS = [5, 10, 20, 30, 50];

export const FORMAT_TIME = {
  day: "DD/MM/YYYY",
  hour: "HH:mm DD/MM/YYYY",
  month: "MM/YYYY",
};

export const RANGES_DEFAULT_DATETIME_PICKER = () => {
  // Get the current date and time
  const currentDate = new Date();

  let today = new Date();
  today.setHours(0, 0, 0, 0);

  let yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);

  return {
    [$t("dashboard.base.label.last_2_week")]: [genAgoTime(20160), currentDate],
    [$t("dashboard.base.label.last_3_hour")]: [genAgoTime(180), currentDate],
    [$t("dashboard.base.label.last_24_hour")]: [genAgoTime(1440), currentDate],
    [$t("dashboard.base.label.last_2_day")]: [genAgoTime(2880), currentDate],
    [$t("dashboard.base.label.today")]: [today, currentDate],
    [$t("dashboard.base.label.yesterday")]: [yesterday, today],
    [$t("dashboard.base.label.last_1_month")]: [genAgoTime(43200), currentDate],
    [$t("dashboard.base.label.last_3_month")]: [
      genAgoTime(129600),
      currentDate,
    ],
    [$t("dashboard.base.label.last_6_month")]: [
      genAgoTime(259200),
      currentDate,
    ],
    [$t("dashboard.base.label.last_12_month")]: [
      genAgoTime(518400),
      currentDate,
    ],
  };
};

export const TIME_CHART_UNIT = {
  HOUR: {
    unit: "hour",
    displayFormats: {
      hour: "HH:mm",
    },
  },
  DAY: {
    unit: "day",
    displayFormats: {
      day: "DD/MM",
    },
  },
  DATE: {
    unit: "day",
    displayFormats: {
      day: "DD/MM/YYYY",
    },
  },
  WEEK: {
    unit: "week",
    displayFormats: {
      week: "[W]WW/YYYY",
    },
  },
  MONTH: {
    unit: "month",
    displayFormats: {
      month: "MM/YYYY",
    },
  },
  YEAR: {
    unit: "year",
    displayFormats: {
      hour: "YYYY",
    },
  },
};

export const GEN_TOP_KUBERNETES_HEADER = (isCPU, isPopupVisible) => [
  {
    text: $t("dashboard.running_kubernetes.label.app_name"),
    sortable: false,
    value: "name",
    class: "header-table",
    minWidth: "120px",
  },
  ...(isPopupVisible
    ? [
        {
          text: $t("dashboard.running_kubernetes.label.pod_name"),
          sortable: false,
          value: "podName",
          class: "header-table",
          minWidth: "120px",
        },
      ]
    : []),
  {
    text: $t("dashboard.running_kubernetes.label.status"),
    sortable: false,
    value: "status",
    class: "header-table",
    minWidth: "95px",
  },
  ...(isPopupVisible
    ? [
        {
          text: $t("dashboard.running_kubernetes.label.node"),
          sortable: false,
          value: "node",
          class: "header-table",
          minWidth: "90px",
        },
      ]
    : [
        {
          text: $t("dashboard.running_kubernetes.label.replicas"),
          sortable: false,
          value: "replicas",
          class: "header-table",
          minWidth: "70px",
        },
      ]),
  {
    text: $t("dashboard.running_kubernetes.label.age"),
    sortable: false,
    value: "age",
    class: "header-table",
    minWidth: "90px",
  },
  {
    text: isCPU
      ? "CPU (millicores)"
      : $t("dashboard.running_kubernetes.label.memory"),
    sortable: false,
    value: "value",
    class: "header-table",
    minWidth: "90px",
  },
];

export const POD_TABLE_HEADER = () => [
  {
    text: $t("dashboard.running_kubernetes.label.name"),
    sortable: false,
    value: "name",
    class: "header-table",
    minWidth: "110px",
  },
  {
    text: $t("dashboard.running_kubernetes.label.node"),
    sortable: false,
    value: "node",
    class: "header-table",
    minWidth: "80px",
  },
  {
    text: $t("dashboard.running_kubernetes.label.namespace"),
    sortable: false,
    value: "namespace",
    class: "header-table",
    minWidth: "80px",
  },
  {
    text: $t("dashboard.running_kubernetes.label.ready"),
    sortable: false,
    value: "ready",
    class: "header-table",
    minWidth: "60px",
  },
  {
    text: "CPU (millicores)",
    sortable: false,
    value: "cpu",
    class: "header-table",
    minWidth: "60px",
  },
  {
    text: $t("dashboard.running_kubernetes.label.memory") + " (MiB)",
    sortable: false,
    value: "memory",
    class: "header-table",
    minWidth: "60px",
  },
  {
    text: $t("dashboard.running_kubernetes.label.status"),
    sortable: false,
    value: "status",
    class: "header-table",
    minWidth: "80px",
  },
];

export const APP_STATUS = {
  Running: "Running",
  Error: "Error",
  Degraded: "Degraded",
  Completed: "Completed",
  Undeployed: "Undeployed",
};

export const APP_TYPE = {
  Deployment: "Deployment",
  StatefulSet: "StatefulSet",
};

export const KUBERNETES_SUMMARY_LABEL = {
  Total: "#3399FF",
  [APP_STATUS.Running]: "#321FDB",
  [APP_STATUS.Completed]: "#2EB85C",
  [APP_STATUS.Error]: "#E55353",
  [APP_TYPE.Deployment]: "#646569",
  [APP_TYPE.StatefulSet]: "#646569",
};

export const APP_STATUS_COLORS = {
  Running: COLORS.PRIMARY,
  Error: COLORS.FAILED,
  Degraded: COLORS.WARNING,
  Completed: COLORS.SUCCEEDED,
  Undeployed: COLORS.GRAY,
};

export const DEFAULT_VALUE_RANGE_PICKER = 7; // days
export const MAX_DURATION_START_END = 30; // days;

export const MILI_CORE_TO_NANO = 1000000;
export const MIB_TO_KI = 1024;
export const CORE_TO_MILI_CORE = 1000;

export const TIME_REFRESH = 60000; // MILISECONDS

export const REPORT_PERIOD = {
  DAY: "DAY",
  WEEK: "WEEK",
  MONTH: "MONTH",
  YEAR: "YEAR",
};

export const BENCH_MARKS_HEADER = [
  {
    text: "Metric",
    sortable: false,
    value: "metric",
    class: "header-table",
    width: "20%",
  },
  {
    text: "Low",
    sortable: false,
    value: "low",
    class: "header-table",
    width: "20%",
  },
  {
    text: "Medium",
    sortable: false,
    value: "medium",
    class: "header-table",
    width: "20%",
  },
  {
    text: "High",
    sortable: false,
    value: "high",
    class: "header-table",
    width: "20%",
  },
  {
    text: "Elite",
    sortable: false,
    value: "elite",
    class: "header-table",
    width: "20%",
  },
];

export const PIPELINE_TYPE = { CI: "CI", CD: "CD" };

export const DORA_BENCH_MARKS_LEVEL_CONFIG = () => ({
  DEPLOYMENT_FREQUENCY: {
    metric: $t("dashboard.dora_cycle.label.deployment_frequency"),
    elite: $t("dashboard.dora_cycle.label.elite_text_desc"),
    high: $t("dashboard.dora_cycle.label.high_text_desc"),
    medium: $t("dashboard.dora_cycle.label.medium_text_desc"),
    low: $t("dashboard.dora_cycle.label.low_text_desc"),
    valueRange: [
      { type: "elite", range: [1, null] },
      { type: "high", range: [1 / 7, 1] },
      { type: "medium", range: [1 / 30, 1 / 7] },
      { type: "low", range: [null, 1 / 30] },
    ],
  },
  LEAD_TIME_CHANGE: {
    metric: $t("dashboard.dora_cycle.label.lead_time_for_change"),
    elite: $t("dashboard.dora_cycle.label.lead_time_elite_text_desc"),
    high: $t("dashboard.dora_cycle.label.lead_time_high_text_desc"),
    medium: $t("dashboard.dora_cycle.label.lead_time_medium_text_desc"),
    low: $t("dashboard.dora_cycle.label.lead_time_low_text_desc"),
    valueRange: [
      { type: "elite", range: [null, 24] },
      { type: "high", range: [24, 24 * 7] },
      { type: "medium", range: [24 * 7, 24 * 30] },
      { type: "low", range: [24 * 30, null] },
    ],
  },
  CHANGE_FAILURE_RATE: {
    metric: $t("dashboard.dora_cycle.label.change_fail_rate"),
    elite: "0 - 5%",
    high: "5 - 10%",
    medium: "10 - 15%",
    low: "15 - 100%",
    valueRange: [
      { type: "elite", range: [0, 5] },
      { type: "high", range: [5, 10] },
      { type: "medium", range: [10, 15] },
      { type: "low", range: [15, 100] },
    ],
  },
});

export const CHART_DESCRIPTION = () => ({
  TOTAL_COUNT: $t("dashboard.base.tooltip.total_count"),
  AVG_BUILD_TIME: $t("dashboard.base.tooltip.avg_build_time"),
  TIME_DEVIATION: $t("dashboard.base.tooltip.time_deviation"),
  RUNNING_STATE: $t("dashboard.base.tooltip.running_state"),
  NUMBER_OF_BUILDS: $t("dashboard.base.tooltip.num_of_build"),
  DAILY_BUILD_FREQUENCY: $t("dashboard.base.tooltip.daily_build"),
  AVERAGE_BUILD_DURATION: $t("dashboard.base.tooltip.avg_build_duration"),
  LONGEST_10_BUILDS: $t("dashboard.base.tooltip.longest_10_build"),
  HONEYCOMB_VIEW: $t("dashboard.base.tooltip.honeycomb_view"),
  AGGREGATE_INFORMATION_OF_MULTIPLE_APPLICATIONS: $t(
    "dashboard.base.tooltip.aggregate_info",
  ),
  APPLICATION_LIST: $t("dashboard.base.tooltip.application_list"),
  TIME_DURATION_TRENDS_OVER_RECENT_N_BUILDS_DEPLOYMENTS: $t(
    "dashboard.base.tooltip.time_duration_trend",
  ),
  LONGEST_10_BUILDS_APPDETAIL: $t(
    "dashboard.base.tooltip.longest_10_builds_app_detail",
  ),
  OVERALL_DORA_METRIC: $t("dashboard.base.tooltip.overall_dora_metric"),
  DEPLOYMENT_FREQUENCY: $t("dashboard.base.tooltip.deployment_frequency"),
  LEAD_TIME_FOR_CHANGE: $t("dashboard.base.tooltip.lead_time_for_change"),
  CHANGE_FAILURE_RATE: $t("dashboard.base.tooltip.change_failure_rate"),
  CYCLE_TIME_OVERVIEW: $t("dashboard.base.tooltip.cycle_time_overview"),
  CI_NUMBER_OF_EXECUTION: $t("dashboard.base.tooltip.ci_number_of_execution"),
  CD_NUMBER_OF_EXECUTION: $t("dashboard.base.tooltip.cd_number_of_execution"),
  BUILD_TIME: $t("dashboard.base.tooltip.build_time"),
  DEPLOYMENT_TIME: $t("dashboard.base.tooltip.deployment_time"),
  CYCLE_TIME_BREAKDOWN: $t("dashboard.base.tooltip.cycle_time_breakdown"),
  TOP_MOST_ACTIVELY_DEVELOPED_APPLICATIONS_RECENTLY: $t(
    "dashboard.base.tooltip.top_most_active_developed",
  ),
  TOP_RECENT_PRS_WITH_SIGNIFICANT_CODE_CHANGES: $t(
    "dashboard.base.tooltip.top_recent_pr",
  ),
  COMMIT_FREQUENCY: $t("dashboard.base.tooltip.commit_frequency"),
  CODING_TIME: $t("dashboard.base.tooltip.coding_time"),
  AVERAGE_REVIEW_DURATION: $t("dashboard.base.tooltip.avg_review_duration"),
  HISTORICAL_WORK_ON_BRANCHES: $t("dashboard.base.tooltip.historical_work"),
  AVERAGE_PRS_SIZE: $t("dashboard.base.tooltip.average_pr_size"),
  ONGOING_PRS: $t("dashboard.base.tooltip.ongoing_pr"),
  TOP_N_CONTRIBUTORS_BY_PRS: $t("dashboard.base.tooltip.top_n_contributors_pr"),
  TOP_N_MOST_DEPLOYED_APPLICATIONS: $t(
    "dashboard.base.tooltip.top_n_most_deployed",
  ),
  TOP_N_MOST_COMMITED_APPLICATIONS: $t(
    "dashboard.base.tooltip.top_n_most_commited",
  ),
  TOP_N_MOST_BUILED_APPLICATIONS: $t("dashboard.base.tooltip.top_n_most_build"),
  LINE_OF_CODE: $t("dashboard.base.tooltip.line_of_code"),
  CODE_CHURN: $t("dashboard.base.tooltip.code_churn"),
  RELIABILITY: $t("dashboard.base.tooltip.reliability"),
  SECURITY: $t("dashboard.base.tooltip.security"),
  SECURITY_HOTSPOTS: $t("dashboard.base.tooltip.security_hotspots"),
  TEST_COVERAGE: $t("dashboard.base.tooltip.test_coverage"),
  MAINTAINABILITY_DEBT: $t("dashboard.base.tooltip.maintainability_debt"),
  MAINTAINABILITY_CODE_SMELLS: $t(
    "dashboard.base.tooltip.maintainability_code_smells",
  ),
  DUPLICATED_BLOCKS: $t("dashboard.base.tooltip.duplicated_blocks"),
  DUPLICATED_LINES: $t("dashboard.base.tooltip.duplicated_lines"),
  TOP_CONTRIBUTOR_OF_WEEK: $t("dashboard.base.tooltip.top_contributor_of_week"),
  TOP_CONTRIBUTOR_OVER_TIME: $t(
    "dashboard.base.tooltip.top_contributor_over_time",
  ),
  TOP_REVIEWER: $t("dashboard.base.tooltip.top_reviewer"),
});

export const DASHBOARD_TITLE_TOOLTIP = () => ({
  APP_STATUS: $t("dashboard.base.tooltip.app_status"),
  CICD_STATUS: $t("dashboard.base.tooltip.cicd"),
  DORA_CYCLE_TIME: $t("dashboard.base.tooltip.dora_cycle_time"),
  CODE_QUALITY: $t("dashboard.base.tooltip.code_quality"),
  CODE_ANALYSIS: $t("dashboard.base.tooltip.code_analysis"),
});

const DEPLOYMENT_FREQUENCY_CONFIG = {
  projectName: "Project",
  profileName: "Profile",
  appName: "Application",
  status: "Status",
  y: "Count",
  finishDate: "Time",
};

const LEAD_TIME_CHANGE_CONFIG = {
  projectName: "Project",
  profileName: "Profile",
  appName: "Application",
  y: "Duration (hours)",
  deployedDate: "Deployed Date",
};

const CHANGE_FAILURE_RATE_CONFIG = {
  projectName: "Project",
  profileName: "Profile",
  appName: "Application",
  numberCI: "Number CI",
  ciFailed: "CI Failed",
  numberCD: "Number CD",
  cdFailed: "CD Failed",
  finishDate: "Executed Time",
};

export const FIELD_EXPORT_CONFIG = {
  TIME_DEVIATION: {
    projectName: "Project",
    profileName: "Profile",
    appName: "Application",
    status: "Status",
    y: "Duration (minutes)",
    timestamp: "Executed Time",
  },
  RUNNING_STATE: {
    projectName: "Project",
    profileName: "Profile",
    appName: "Application",
    status: "Status",
    y: "Quantity",
    timestamp: "Executed Time",
  },
  DAILY_BUILD_FREQUENCY: {
    projectName: "Project",
    profileName: "Profile",
    appName: "Application",
    status: "Status",
    y: "Quantity",
    timestamp: "Executed Time",
  },
  AVERAGE_BUILD_DURATION: {
    projectName: "Project",
    profileName: "Profile",
    appName: "Application",
    status: "Status",
    y: "Avg duration (minutes)",
    timestamp: "Executed Time",
  },
  LONGEST_10_BUILDS: {
    projectName: "Project",
    profileName: "Profile",
    appName: "Application",
    status: "Status",
    duration: "Duration (minutes)",
    timestamp: "Executed Time",
  },
  AGGREGATE_INFORMATION_OF_MULTIPLE_APPLICATIONS: {
    projectName: "Project",
    profileName: "Profile",
    appName: "Application",
    status: "Status",
    y: "Duration (minutes)",
    timestamp: "Executed Time",
  },
  APPLICATION_LIST: {
    projectName: "Project",
    profileName: "Profile",
    appName: "Application",
    imageName: "Image Name",
    imageTag: "Image Tag",
    branch: "Branch",
    commit: "Commit",
    status: "Status",
    duration: "Duration (minutes)",
    pipeLineMessage: "Message",
    // url: "Commit",
  },
  HONEYCOMB_VIEW: {
    projectName: "Project",
    profileName: "Profile",
    appName: "Application",
    viewReplica: "Replica",
    status: "Status",
    type: "Type",
    namespace: "Namespace",
  },
  TIME_DURATION_TRENDS_OVER_RECENT_N_BUILDS_DEPLOYMENTS: {
    projectName: "Project",
    profileName: "Profile",
    appName: "Application",
    status: "Status",
    y: "Duration (minutes)",
    timestamp: "Executed Time",
  },
  DEPLOYMENT_FREQUENCY: DEPLOYMENT_FREQUENCY_CONFIG,
  DEPLOYMENT_FREQUENCY_BAR: omit(DEPLOYMENT_FREQUENCY_CONFIG, ["finishDate"]),
  LEAD_TIME_CHANGE: LEAD_TIME_CHANGE_CONFIG,
  LEAD_TIME_CHANGE_BAR: omit(LEAD_TIME_CHANGE_CONFIG, ["deployedDate"]),
  CHANGE_FAILURE_RATE: CHANGE_FAILURE_RATE_CONFIG,
  CHANGE_FAILURE_RATE_BAR: omit(CHANGE_FAILURE_RATE_CONFIG, ["finishDate"]),
  BUILD_TIME: {
    projectName: "Project",
    profileName: "Profile",
    appName: "Application",
    status: "Status",
    y: "Build Time (minutes)",
    x: "Executed Time",
  },
  DEPLOYMENT_TIME: {
    projectName: "Project",
    profileName: "Profile",
    appName: "Application",
    status: "Status",
    y: "Deployment Time (minutes)",
    x: "Executed Time",
  },
  CYCLE_TIME_BREAKDOWN: {
    mergeDate: "Merge Date",
    prTitle: "PR Title",
    prAuthor: "PR Author",
    codingDuration: "Coding Time",
    reviewingDuration: "Review Time",
    url: "URL",
  },
  TOP_DEVELOPED_APP: {
    projectName: "Project",
    profileName: "Profile",
    x: "Application",
    repo: "Repository",
    y: "Line Of Code",
  },
  TOP_RECENT_PR: {
    projectName: "Project",
    profileName: "Profile",
    appName: "Application",
    repo: "Repository",
    branch: "Branch",
    x: "PR Title",
    y: "Line Of Code",
  },
};

export const COLOR_COLLECTOR = [
  "#0095AC", // Common Cyan
  "#FD7E14", // Orange
  "#E83E8C", // Pink
  "#FFD700", // Gold
  "#8A2BE2", // Blue Violet
  "#FF4500", // Orange Red
  "#2E8B57", // Sea Green
  "#00CED1", // Dark Turquoise
  "#FF69B4", // Hot Pink
  "#CD5C5C", // Indian Red
  "#4B0082", // Indigo
  "#7FFF00", // Chartreuse
  "#FF1493", // Deep Pink
  "#1E90FF", // Dodger Blue
  "#00FA9A", // Medium Spring Green
  "#FF6347", // Tomato
  "#4682B4", // Steel Blue
  "#DA70D6", // Orchid
  "#32CD32", // Lime Green
  "#00FF7F", // Spring Green
  "#FF00FF", // Magenta
  "#00FFFF", // Cyan
  "#7B68EE", // Medium Slate Blue
  "#DC143C", // Crimson
  "#FF8C00", // Dark Orange
  "#8B0000", // Dark Red
  "#00BFFF", // Deep Sky Blue
  "#ADFF2F", // Green Yellow
  "#DDA0DD", // Plum
  "#F08080", // Light Coral
  "#FF7F50", // Coral
  "#B0E0E6", // Powder Blue
  "#DAA520", // Goldenrod
  "#778899", // Light Slate Gray
  "#BA55D3", // Medium Orchid
  "#FFB6C1", // Light Pink
  "#FA8072", // Salmon
  "#E9967A", // Dark Salmon
  "#9400D3", // Dark Violet
  "#FFDEAD", // Navajo White
  "#98FB98", // Pale Green
  "#AFEEEE", // Pale Turquoise
  "#DB7093", // Pale Violet Red
  "#FFDAB9", // Peach Puff
  "#CD853F", // Peru
  "#FFC0CB", // Pink
  "#DDA0DD", // Plum
  "#B0C4DE", // Light Steel Blue
];

export const DEFAULT_CUSTOMIZED_METRIC_DORA_CYCLETIME = {
  "DORA Metrics": {
    tKey: "dashboard.base.label.dora_metrics",
    "Overall DORA Metric": {
      show: true,
      fixed: false,
      span: 1,
      tKey: "dashboard.dora_cycle.label.overall_dora_metric",
    },
    "Deployment Frequency": {
      show: true,
      fixed: false,
      span: 1,
      tKey: "dashboard.dora_cycle.label.deployment_frequency",
    },
    "Lead time for change": {
      show: true,
      fixed: false,
      span: 1,
      tKey: "dashboard.dora_cycle.label.lead_time_for_change",
    },
    "Change Failure Rate": {
      show: true,
      fixed: false,
      span: 1,
      tKey: "dashboard.dora_cycle.label.change_fail_rate",
    },
  },
  "Cycle Time Metrics": {
    tKey: "dashboard.base.label.cycle_time_metrics",
    "CI Number of Execution": {
      show: true,
      fixed: false,
      span: 1,
      tKey: "dashboard.dora_cycle.label.ci_number_execution",
    },
    "CD Number of Execution": {
      show: true,
      fixed: false,
      span: 1,
      tKey: "dashboard.dora_cycle.label.cd_number_execution",
    },
    "Cycle Time Overview": {
      show: true,
      fixed: false,
      span: 2,
      tKey: "dashboard.dora_cycle.label.cycle_time_overview",
    },
    "Build Time": {
      show: true,
      fixed: false,
      span: 1,
      tKey: "dashboard.dora_cycle.label.build_time",
    },
    "Deployment Time": {
      show: true,
      fixed: false,
      span: 1,
      tKey: "dashboard.dora_cycle.label.deployment_time",
    },
    "Cycle Time Breakdown": {
      show: true,
      fixed: false,
      span: 2,
      tKey: "dashboard.dora_cycle.label.cycle_time_breakdown",
    },
  },
  "Top App and PR": {
    tKey: "dashboard.base.label.top_app_pr",
    "Top most actively developed applications recently": {
      show: true,
      fixed: false,
      span: 2,
      tKey: "dashboard.dora_cycle.label.top_most_active",
    },
    "Top recent PRs with significant code changes": {
      show: true,
      fixed: false,
      span: 2,
      tKey: "dashboard.dora_cycle.label.top_recent_pr",
    },
  },
};

export const CUSTOMIZED_METRIC_CODE_QUALITY = {
  "Code Quality": {
    tKey: "dashboard.code_quality.label.code_quality",
    "Commit Frequency": {
      tKey: "dashboard.code_quality.label.commit_frequency",
      show: true,
      fixed: false,
      span: 1,
    },
    "Coding Time": {
      tKey: "dashboard.dora_cycle.label.coding_time",
      show: true,
      fixed: false,
      span: 1,
    },
    "Average Review Duration": {
      tKey: "dashboard.code_quality.label.avg_review_duration",
      show: true,
      fixed: false,
      span: 1,
    },
    "Historical Work On Branches": {
      tKey: "dashboard.code_quality.label.history_work_branch",
      show: true,
      fixed: false,
      span: 1,
    },
    "Average PRs Size": {
      tKey: "dashboard.code_quality.label.avg_pr_size",
      show: true,
      fixed: false,
      span: 1,
    },
    "Ongoing PRs": {
      tKey: "dashboard.code_quality.label.ongoing_pr",
      show: true,
      fixed: false,
      span: 1,
    },
    "Top 10 Contributors by PRs": {
      tKey: "dashboard.code_quality.label.top_10_contributor",
      show: true,
      fixed: false,
      span: 1,
    },
    "Top 10 Most Deployed Applications": {
      tKey: "dashboard.code_quality.label.top_10_deploy",
      show: true,
      fixed: false,
      span: 1,
    },
    "Top 10 Most Committed Applications": {
      tKey: "dashboard.code_quality.label.top_10_commit",
      show: true,
      fixed: false,
      span: 1,
    },
    "Top 10 Most Builed Applications": {
      tKey: "dashboard.base.label.top_10_build_app",
      show: true,
      fixed: false,
      span: 1,
    },
  },
};

export const CUSTOMIZED_METRIC_CODE_QUALITY_2 = {
  "Code Quality": {
    tKey: "dashboard.code_quality.label.code_quality",
    "Line Of Code": {
      tKey: "dashboard.code_analysis.label.line_of_code",
      show: true,
      row: 1,
    },
    "Code Churn": {
      tKey: "dashboard.code_analysis.label.code_churn",
      show: true,
      row: 1,
    },
    Reliability: {
      tKey: "dashboard.code_analysis.label.reliability",
      show: true,
    },
    Security: {
      tKey: "dashboard.code_analysis.label.security",
      show: true,
    },
    "Security Hotspots": {
      tKey: "dashboard.code_analysis.label.security_hotspots",
      show: true,
    },
    Test: {
      tKey: "dashboard.code_analysis.label.test",
      show: true,
    },
    "Maintainability - Debt": {
      tKey: "dashboard.code_analysis.label.maintainability_debt",
      show: true,
    },
    "Maintainability - Code Smells": {
      tKey: "dashboard.code_analysis.label.maintainability_code_smell",
      show: true,
    },
    "Duplicated Blocks": {
      tKey: "dashboard.code_analysis.label.duplicate_block",
      show: true,
    },
    "Duplicated Lines": {
      tKey: "dashboard.code_analysis.label.duplicate_line",
      show: true,
    },
    "Top Contributor Of Week": {
      tKey: "dashboard.code_analysis.label.top_week_contributor",
      show: true,
    },
    "Top Contributor Over Time": {
      tKey: "dashboard.code_analysis.label.top_overtime_contributor",
      show: true,
    },
    "Top Reviewer": {
      tKey: "dashboard.code_analysis.label.top_reviewer",
      show: true,
    },
  },
};

export const CUSTOMIZED_METRIC_APPLICATION_STATUS = {
  "Application Status": {
    tKey: "dashboard.application.label.app_dashboard",
    "Top 10 Highest Usage CPU": {
      tKey: "dashboard.running_kubernetes.label.top_10_cpu",
      show: true,
      col: 1,
    },
    "Top 10 Highest Usage Memory": {
      tKey: "dashboard.running_kubernetes.label.top_10_memory",
      show: true,
      col: 1,
    },
    "Application List": {
      tKey: "profile.detail__microservice.title.list",
      show: true,
    },
    "CPU Usage": {
      tKey: "dashboard.running_kubernetes.label.cpu_usage",
      show: true,
      col: 1,
    },
    "Memory Usage": {
      tKey: "dashboard.running_kubernetes.label.memory_usage",
      show: true,
      col: 1,
    },
  },
};

export const CUSTOMIZED_METRIC_CICD_STATUS = {
  "CI/CD Status": {
    tKey: "menu.cicd_status",
    "Total count": {
      tKey: "dashboard.cicd.label.total_count",
      show: true,
      row: 1,
      col: 1,
    },
    "Avg. build time": {
      tKey: "dashboard.cicd.label.avg_build_time",
      show: true,
      row: 1,
      col: 1,
    },
    "Time deviation": {
      tKey: "dashboard.cicd.label.time_deviation",
      show: true,
      row: 1,
      col: 2,
    },
    "Running State": {
      tKey: "dashboard.cicd.label.running_state",
      show: true,
      row: 1,
      col: 2,
    },
    "Honeycomb View": {
      tKey: "dashboard.cicd.label.honeycomb_view",
      show: true,
      row: 1,
      col: 3,
    },
    "Number of builds": {
      tKey: "dashboard.cicd.label.number_of_builds",
      show: true,
      row: 1,
      col: 1,
    },
    "Daily build frequency": {
      tKey: "dashboard.cicd.label.daily_build_frequency",
      show: true,
      row: 1,
      col: 2,
    },
    "Average build duration": {
      tKey: "dashboard.cicd.label.avg_build_duration",
      show: true,
      row: 1,
      col: 2,
    },
    "Longest 10 builds": {
      tKey: "dashboard.cicd.label.longest_10_build",
      show: true,
      row: 1,
      col: 2,
    },
    "Aggregate Info": {
      tKey: "dashboard.cicd.label.aggregate_info",
      show: true,
    },
    "Application List": {
      tKey: "profile.detail__microservice.title.list",
      show: true,
    },
  },
};

export const CODE_QUALITITY_TABLE = () => ({
  ON_GOING_PR_TABLE_HEADER: [
    {
      text: $t("dashboard.dora_cycle.label.pr_title"),
      sortable: false,
      value: "prTitle",
      class: "header-table",
      minWidth: "140px",
    },
    {
      text: $t("dashboard.dora_cycle.label.pr_author"),
      sortable: false,
      value: "prAuthor",
      class: "header-table",
      minWidth: "140px",
    },
    {
      text: $t("dashboard.base.label.created_date"),
      sortable: false,
      value: "prCreatedDate",
      class: "header-table",
      minWidth: "70px",
    },
    {
      text: $t("dashboard.code_analysis.label.reviewer_number"),
      sortable: false,
      value: "numberOfReviewers",
      class: "header-table",
      minWidth: "150px",
    },
    {
      text: $t("dashboard.code_analysis.label.comment_number"),
      sortable: false,
      value: "numberOfComments",
      class: "header-table",
      minWidth: "100px",
    },
    {
      text: $t("dashboard.running_kubernetes.label.status"),
      sortable: false,
      value: "status",
      class: "header-table",
      minWidth: "100px",
    },
    {
      text: "URL",
      sortable: false,
      value: "url",
      class: "header-table",
      minWidth: "100px",
    },
  ],
  HISTORICAL_BRANCH_TABLE_HEADER: [
    {
      text: $t("dashboard.code_quality.label.branch_name"),
      sortable: false,
      value: "branchName",
      class: "header-table",
      minWidth: "140px",
    },
    {
      text: $t("dashboard.base.label.created_date"),
      sortable: false,
      value: "createdDate",
      class: "header-table",
      minWidth: "140px",
    },
    {
      text: $t("dashboard.code_quality.label.last_commit"),
      sortable: false,
      value: "lastCommittedDate",
      class: "header-table",
      minWidth: "70px",
    },
    {
      text: $t("dashboard.code_analysis.label.commit_number"),
      sortable: false,
      value: "noOfCommits",
      class: "header-table",
      minWidth: "150px",
    },
    {
      text: $t("dashboard.code_quality.label.contributor_number"),
      sortable: false,
      value: "noOfContributors",
      class: "header-table",
      minWidth: "100px",
    },
    {
      text: $t("dashboard.code_quality.label.merge_status"),
      sortable: false,
      value: "status",
      class: "header-table",
      minWidth: "100px",
    },
    {
      text: "URL",
      sortable: false,
      value: "url",
      class: "header-table",
      minWidth: "100px",
    },
  ],
  CONTRIBUTORS_TABLE_HEADER: [
    {
      text: $t("dashboard.code_quality.label.contributor_name"),
      sortable: false,
      value: "userName",
      class: "header-table",
      minWidth: "140px",
    },
    {
      text: $t("dashboard.code_quality.label.pr_number"),
      sortable: false,
      value: "noOfPRs",
      class: "header-table",
      minWidth: "140px",
    },
    {
      text: $t("dashboard.code_quality.label.loc_added"),
      sortable: false,
      value: "locAdded",
      class: "header-table",
      minWidth: "70px",
    },
    {
      text: $t("dashboard.code_quality.label.loc_deleted"),
      sortable: false,
      value: "locDeleted",
      class: "header-table",
      minWidth: "150px",
    },
    {
      text: $t("dashboard.code_quality.label.file_change_number"),
      sortable: false,
      value: "noFiles",
      class: "header-table",
      minWidth: "100px",
    },
    {
      text: "URL",
      sortable: false,
      value: "url",
      class: "header-table",
      minWidth: "100px",
    },
  ],
  MOST_DEPLOY_TABLE_HEADER: [
    {
      text: $t("dashboard.running_kubernetes.label.app_name"),
      sortable: false,
      value: "appName",
      class: "header-table",
      minWidth: "140px",
    },
    {
      text: $t("dashboard.code_quality.label.deployment_number"),
      sortable: false,
      value: "noOfDeployments",
      class: "header-table",
      minWidth: "140px",
    },
    {
      text: $t("dashboard.code_quality.label.deployed_date"),
      sortable: false,
      value: "lastDeployedDate",
      class: "header-table",
      minWidth: "70px",
    },
    {
      text: $t("dashboard.code_quality.label.version"),
      sortable: false,
      value: "version",
      class: "header-table",
      minWidth: "150px",
    },
    {
      text: $t("dashboard.code_quality.label.deployment_env"),
      sortable: false,
      value: "profileName",
      class: "header-table",
      minWidth: "100px",
    },
    {
      text: "URL",
      sortable: false,
      value: "url",
      class: "header-table",
      minWidth: "100px",
    },
  ],
  MOST_COMMITED_TABLE_HEADER: [
    {
      text: $t("dashboard.running_kubernetes.label.app_name"),
      sortable: false,
      value: "applicationName",
      class: "header-table",
      minWidth: "140px",
    },
    {
      text: $t("dashboard.code_analysis.label.commit_number"),
      sortable: false,
      value: "noOfCommits",
      class: "header-table",
      minWidth: "140px",
    },
    {
      text: $t("dashboard.code_quality.label.last_commit_date"),
      sortable: false,
      value: "lastCommittedDate",
      class: "header-table",
      minWidth: "70px",
    },
    {
      text: $t("dashboard.code_quality.label.contributor_number"),
      sortable: false,
      value: "noOfContributor",
      class: "header-table",
      minWidth: "150px",
    },
    {
      text: $t("dashboard.code_quality.label.loc_changed"),
      sortable: false,
      value: "noOfChanged",
      class: "header-table",
      minWidth: "100px",
    },
    {
      text: "URL",
      sortable: false,
      value: "url",
      class: "header-table",
      minWidth: "100px",
    },
  ],
  MOST_BUILD_TABLE_HEADER: [
    {
      text: $t("dashboard.running_kubernetes.label.app_name"),
      sortable: false,
      value: "appName",
      class: "header-table",
      minWidth: "140px",
    },
    {
      text: $t("dashboard.cicd.label.number_of_builds"),
      sortable: false,
      value: "noOfBuild",
      class: "header-table",
      minWidth: "140px",
    },
    {
      text: $t("dashboard.code_quality.label.last_build_date"),
      sortable: false,
      value: "lastBuiltDate",
      class: "header-table",
      minWidth: "70px",
    },
    {
      text: $t("dashboard.code_quality.label.build_status"),
      sortable: false,
      value: "status",
      class: "header-table",
      minWidth: "150px",
    },
    {
      text: $t("dashboard.cicd.label.avg_build_time"),
      sortable: false,
      value: "builtDuration",
      class: "header-table",
      minWidth: "100px",
    },
    {
      text: "URL",
      sortable: false,
      value: "url",
      class: "header-table",
      minWidth: "100px",
    },
  ],
});
export const METRIC_RANKING_DETAILS = () => ({
  RELIABILITY: $t("dashboard.code_analysis.tooltip.reliability"),
  SECURITY: $t("dashboard.code_analysis.tooltip.security"),
  MAINTAINABILITY: $t("dashboard.code_analysis.tooltip.maintainability"),
  DUPLICATE: $t("dashboard.code_analysis.tooltip.duplicate"),
  TEST: $t("dashboard.code_analysis.tooltip.test"),
});
